import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemButton,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { userManagementService, CustomerV2 } from '../../services/userManagementService';

interface Step1CustomerSelectionProps {
  selectedCustomers: CustomerV2[];
  onSelectionChange: (customers: CustomerV2[]) => void;
}

const Step1CustomerSelection: React.FC<Step1CustomerSelectionProps> = ({
  selectedCustomers,
  onSelectionChange,
}) => {
  const [customers, setCustomers] = useState<CustomerV2[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  // Load initial customers
  useEffect(() => {
    loadCustomers();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    if (searchTerm.trim().length >= 2) {
      const timer = setTimeout(() => {
        searchCustomers(searchTerm);
      }, 500);
      searchDebounceTimerRef.current = timer;
    } else if (searchTerm.trim().length === 0) {
      loadCustomers();
    }

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const result = await userManagementService.fetchCustomers('', 1, 100);
      setCustomers(result.items);
    } catch (error: any) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchCustomers = async (term: string) => {
    try {
      setSearchLoading(true);
      const abortController = new AbortController();
      searchAbortControllerRef.current = abortController;

      const result = await userManagementService.fetchCustomers(term, 1, 100);
      
      if (!abortController.signal.aborted) {
        setCustomers(result.items);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Error searching customers:', error);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleToggleCustomer = (customer: CustomerV2) => {
    const isSelected = selectedCustomers.some((c) => c.id === customer.id);
    if (isSelected) {
      onSelectionChange(selectedCustomers.filter((c) => c.id !== customer.id));
    } else {
      onSelectionChange([...selectedCustomers, customer]);
    }
  };

  const handleRemoveCustomer = (customerId: string) => {
    onSelectionChange(selectedCustomers.filter((c) => c.id !== customerId));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 1 of 3: Select Customers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select one or more customers to create a client user for. You'll choose contact information in the next step.
      </Typography>

      <TextField
        fullWidth
        label="Search Customers"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Type to search customers..."
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
        }}
      />

      {selectedCustomers.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Customers ({selectedCustomers.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedCustomers.map((customer) => (
              <Chip
                key={customer.id}
                label={customer.red_rock_full_entity_name || customer.company_name || 'Unknown'}
                onDelete={() => handleRemoveCustomer(customer.id)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : customers.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No customers found' : 'No customers available'}
            </Typography>
          </Box>
        ) : (
          <List>
            {customers.map((customer) => {
              const isSelected = selectedCustomers.some((c) => c.id === customer.id);
              return (
                <ListItem key={customer.id} disablePadding>
                  <ListItemButton onClick={() => handleToggleCustomer(customer)}>
                    <Checkbox checked={isSelected} />
                    <ListItemText
                      primary={customer.red_rock_full_entity_name || customer.company_name || 'Unknown'}
                      secondary={customer.company_name && customer.red_rock_full_entity_name !== customer.company_name ? customer.company_name : undefined}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Step1CustomerSelection;

